class AltCatalogueHoverMenu extends HTMLElement {
  constructor() {
    super();

    this.openTimer = null;
    this.closeTimer = null;

    /*
     * Change this if your main navigation item
     * is called something other than "Shop".
     */
    this.shopLabel = 'shop';

    this.desktopQuery = window.matchMedia(
      '(min-width: 990px) and (hover: hover) and (pointer: fine)'
    );
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-catalogue-panel]');

    this.parentLinks = Array.from(
      this.querySelectorAll('[data-catalogue-parent]')
    );

    this.categoryPanels = Array.from(
      this.querySelectorAll('[data-category-panel]')
    );

    this.header = this.closest('header-component');

    if (!this.panel || !this.header) return;

    /*
     * Wait until Horizon's header menu has rendered
     * before looking for the existing Shop link.
     */
    requestAnimationFrame(() => {
      this.bindShopTrigger();
    });

    this.parentLinks.forEach((link) => {
      link.addEventListener(
        'pointerenter',
        this.handleCategoryEnter
      );

      link.addEventListener(
        'focus',
        this.handleCategoryFocus
      );
    });

    this.panel.addEventListener(
      'pointerenter',
      this.handlePanelEnter
    );

    this.panel.addEventListener(
      'pointerleave',
      this.handlePanelLeave
    );

    this.panel.addEventListener(
      'focusin',
      this.handlePanelFocusIn
    );

    this.panel.addEventListener(
      'focusout',
      this.handlePanelFocusOut
    );

    document.addEventListener(
      'keydown',
      this.handleKeyDown
    );

    window.addEventListener(
      'resize',
      this.updatePanelPosition
    );

    window.addEventListener(
      'scroll',
      this.updatePanelPosition,
      { passive: true }
    );

    const firstParent = this.parentLinks[0];

    if (firstParent) {
      this.activateCategory(
        Number(firstParent.dataset.categoryIndex)
      );
    }
  }

  disconnectedCallback() {
    this.unbindShopTrigger();

    this.parentLinks?.forEach((link) => {
      link.removeEventListener(
        'pointerenter',
        this.handleCategoryEnter
      );

      link.removeEventListener(
        'focus',
        this.handleCategoryFocus
      );
    });

    this.panel?.removeEventListener(
      'pointerenter',
      this.handlePanelEnter
    );

    this.panel?.removeEventListener(
      'pointerleave',
      this.handlePanelLeave
    );

    this.panel?.removeEventListener(
      'focusin',
      this.handlePanelFocusIn
    );

    this.panel?.removeEventListener(
      'focusout',
      this.handlePanelFocusOut
    );

    document.removeEventListener(
      'keydown',
      this.handleKeyDown
    );

    window.removeEventListener(
      'resize',
      this.updatePanelPosition
    );

    window.removeEventListener(
      'scroll',
      this.updatePanelPosition
    );

    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  bindShopTrigger() {
    const headerMenus = Array.from(
      this.header.querySelectorAll('header-menu')
    );

    this.headerMenu =
      headerMenus.find(
        (menu) => menu.getClientRects().length > 0
      ) || headerMenus[0];

    if (!this.headerMenu) {
      console.warn(
        'ALT CVLT catalogue menu: Horizon header-menu was not found.'
      );

      return;
    }

    const menuLinks = Array.from(
      this.headerMenu.querySelectorAll(
        '.menu-list__link'
      )
    );

    this.shopTrigger = menuLinks.find((link) => {
      const text = link.textContent
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      return text === this.shopLabel;
    });

    if (!this.shopTrigger) {
      console.warn(
        'ALT CVLT catalogue menu: Could not find the main navigation item "Shop".'
      );

      return;
    }

    this.shopItem =
      this.shopTrigger.closest(
        '.menu-list__list-item'
      ) || this.shopTrigger;

    this.shopTrigger.classList.add(
      'alt-catalogue-shop-trigger'
    );

    this.shopTrigger.setAttribute(
      'aria-haspopup',
      'true'
    );

    this.shopTrigger.setAttribute(
      'aria-expanded',
      'false'
    );

    this.shopItem.addEventListener(
      'pointerenter',
      this.handleShopEnter
    );

    this.shopItem.addEventListener(
      'pointerleave',
      this.handleShopLeave
    );

    this.shopTrigger.addEventListener(
      'focus',
      this.handleShopFocus
    );

    this.shopTrigger.addEventListener(
      'blur',
      this.handleShopBlur
    );

    this.otherMenuLinks = menuLinks.filter(
      (link) => link !== this.shopTrigger
    );

    this.otherMenuLinks.forEach((link) => {
      link.addEventListener(
        'pointerenter',
        this.handleOtherMenuEnter
      );
    });
  }

  unbindShopTrigger() {
    if (this.shopItem) {
      this.shopItem.removeEventListener(
        'pointerenter',
        this.handleShopEnter
      );

      this.shopItem.removeEventListener(
        'pointerleave',
        this.handleShopLeave
      );
    }

    if (this.shopTrigger) {
      this.shopTrigger.removeEventListener(
        'focus',
        this.handleShopFocus
      );

      this.shopTrigger.removeEventListener(
        'blur',
        this.handleShopBlur
      );
    }

    this.otherMenuLinks?.forEach((link) => {
      link.removeEventListener(
        'pointerenter',
        this.handleOtherMenuEnter
      );
    });
  }

  handleShopEnter = () => {
    if (!this.desktopQuery.matches) return;

    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);

    this.openTimer = setTimeout(() => {
      this.openMenu();
    }, 35);
  };

  handleShopLeave = () => {
    if (!this.desktopQuery.matches) return;

    this.scheduleClose();
  };

  handleShopFocus = () => {
    if (!this.desktopQuery.matches) return;

    clearTimeout(this.closeTimer);

    this.openMenu();
  };

  handleShopBlur = (event) => {
    if (!this.desktopQuery.matches) return;

    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    ) {
      return;
    }

    this.scheduleClose();
  };

  handlePanelEnter = () => {
    if (!this.desktopQuery.matches) return;

    clearTimeout(this.closeTimer);
  };

  handlePanelLeave = () => {
    if (!this.desktopQuery.matches) return;

    this.scheduleClose();
  };

  handlePanelFocusIn = () => {
    clearTimeout(this.closeTimer);
  };

  handlePanelFocusOut = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      (
        this.contains(event.relatedTarget) ||
        this.shopItem?.contains(event.relatedTarget)
      )
    ) {
      return;
    }

    this.closeMenu();
  };

  handleOtherMenuEnter = () => {
    if (this.dataset.open !== 'true') return;

    this.closeMenu();
  };

  handleCategoryEnter = (event) => {
    const index = Number(
      event.currentTarget.dataset.categoryIndex
    );

    this.activateCategory(index);
  };

  handleCategoryFocus = (event) => {
    const index = Number(
      event.currentTarget.dataset.categoryIndex
    );

    this.activateCategory(index);
  };

  handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.dataset.open !== 'true') return;

    this.closeMenu();

    this.shopTrigger?.focus();
  };

  scheduleClose() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);

    this.closeTimer = setTimeout(() => {
      this.closeMenu();
    }, 180);
  }

  openMenu() {
    if (!this.desktopQuery.matches) return;
    if (!this.shopTrigger) return;

    clearTimeout(this.closeTimer);

    this.updatePanelPosition();

    this.panel.hidden = false;
    this.dataset.open = 'true';

    this.shopTrigger.setAttribute(
      'aria-expanded',
      'true'
    );

    this.shopTrigger.classList.add(
      'is-catalogue-open'
    );
  }

  closeMenu() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);

    this.dataset.open = 'false';

    if (this.shopTrigger) {
      this.shopTrigger.setAttribute(
        'aria-expanded',
        'false'
      );

      this.shopTrigger.classList.remove(
        'is-catalogue-open'
      );
    }

    if (this.panel) {
      this.panel.hidden = true;
    }
  }

  activateCategory(index) {
    if (Number.isNaN(index)) return;

    const matchingParent = this.parentLinks.find(
      (link) =>
        Number(link.dataset.categoryIndex) === index
    );

    if (!matchingParent) return;

    this.parentLinks.forEach((link) => {
      const isActive =
        Number(link.dataset.categoryIndex) === index;

      link.classList.toggle(
        'is-active',
        isActive
      );
    });

    this.categoryPanels.forEach((panel) => {
      const isActive =
        Number(panel.dataset.categoryPanel) === index;

      panel.hidden = !isActive;
    });
  }

  updatePanelPosition = () => {
    if (!this.header) return;

    const rect =
      this.header.getBoundingClientRect();

    const top = Math.max(
      0,
      Math.round(rect.bottom)
    );

    this.style.setProperty(
      '--alt-catalogue-panel-top',
      `${top}px`
    );
  };
}

if (
  !customElements.get(
    'alt-catalogue-hover-menu'
  )
) {
  customElements.define(
    'alt-catalogue-hover-menu',
    AltCatalogueHoverMenu
  );
}